// ─────────────────────────────────────────────────────────────────────────────
// THE DOUBLE-BLIND RANKING CALL (Ticket 6 #3 + #4).
//
//   POST <runtime>/app/rank-candidates      Authorization: Bearer <supabase jwt>
//   body: {cards: [...], publisher, version}
//   → {ok, order: [{id, score, why}], filtered, version?, degraded}
//
// DIRECTION OF TRAVEL — the invariant this whole ticket exists to protect:
// the candidate SET travels TO the fan's agent. The profile does not travel at
// all. We send cards the show already published to everyone; we receive an
// ordering. At no point does this module transmit, derive, or even hold the
// fan's interest profile — it never asks for one.
//
// WHY INLINE CARDS AND NOT `setUrl`. The endpoint accepts either. We send the
// cards inline deliberately: on the `setUrl` path the DO fetches the set itself,
// which means the show's log gains one request PER RANKING — still anonymous,
// but a count that tracks fan activity. Inline, the show sees exactly one static
// asset GET from a browser, which it would have served to any visitor anyway.
// Fewer show-side events is the stronger reading of "log nothing per-user".
//
// WHY-STRINGS ARE NOT WRITTEN HERE. `order[].why` is produced by the runtime's
// `whyString()`, a strict projection of the components that actually scored the
// card. This module passes it through verbatim. There is deliberately no
// copy-generation function in this file: inventing a plausible reason post-hoc
// is precisely the failure the ticket forbids, so the capability is absent
// rather than merely unused.
// ─────────────────────────────────────────────────────────────────────────────

import {RANK_CANDIDATES_ENDPOINT} from './config'
import {assertRuntimeOrigin} from './showFence'
import type {Card} from './candidateSet'

export interface RankedEntry {
  id: string
  score: number
  /** The runtime's why-string. A projection of the real scoring trace. */
  why: string
}

export interface RankResult {
  ok: boolean
  order: RankedEntry[]
  /** Muted cards, removed rather than down-ranked (hide-not-rank). */
  filtered: number
  /**
   * ONLY present on the `setUrl` path. The DO derives `version` from the set it
   * fetched itself; on the inline-`cards` path it stays null and is omitted
   * from the response. We send `version` for the audit record's benefit but
   * must NOT expect it back — the caller already knows which version it sent.
   */
  version?: number
  degraded: boolean
}

export class RankError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'RankError'
    this.status = status
  }
}

/**
 * Rank the show's candidate set inside the fan's OWN agent.
 *
 * @param token  the fan's Supabase bearer — the runtime maps it to their agent
 *               server-side, so no agent handle is (or can be) named here.
 */
export async function rankCandidates(
  {cards, publisher, version}: {cards: Card[]; publisher: string | null; version?: number},
  token: string,
  {signal}: {signal?: AbortSignal} = {},
): Promise<RankResult> {
  assertRuntimeOrigin(RANK_CANDIDATES_ENDPOINT)

  const res = await fetch(RANK_CANDIDATES_ENDPOINT, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    // No `agent` field: the token-mapped agent is the fan's own, which is the
    // only DO this request may ever reach.
    body: JSON.stringify({cards, publisher, version}),
    signal,
  })

  const body = await res.json().catch(() => ({}) as Record<string, unknown>)

  if (!res.ok) {
    const detail = typeof body?.error === 'string' ? body.error : `rank failed (${res.status})`
    throw new RankError(detail, res.status)
  }

  return {
    ok: Boolean(body?.ok),
    order: Array.isArray(body?.order) ? (body.order as RankedEntry[]) : [],
    filtered: Number(body?.filtered ?? 0),
    version: Number.isFinite(Number(body?.version)) ? Number(body.version) : undefined,
    degraded: Boolean(body?.degraded),
  }
}

/**
 * Join a returned order back onto the local cards, preserving the runtime's
 * ordering exactly.
 *
 * Cards absent from `order` were MUTED (hide-not-rank) and are dropped, not
 * appended — appending them would quietly undo a mute the fan asked for.
 */
export function applyOrder<T extends Card>(
  cards: T[],
  order: RankedEntry[],
  idOf: (card: T, index: number) => string,
): Array<{card: T; why: string; score: number}> {
  const byId = new Map<string, T>()
  cards.forEach((card, i) => byId.set(idOf(card, i), card))

  const out: Array<{card: T; why: string; score: number}> = []
  for (const entry of order) {
    const card = byId.get(entry.id)
    if (card) out.push({card, why: entry.why, score: entry.score})
  }
  return out
}
